// handlers.ts
import { useCallback } from "react";
import { CustomerFormData } from "src/shared/services/interfacev2";
import { sanitizeName, sanitizePhone, sanitizePincode } from "./validationUtils";

export const useFormHandlers = (
  formData: CustomerFormData,
  setFormData: React.Dispatch<React.SetStateAction<CustomerFormData>>,
  setIsDuplicatePhone: (val: boolean) => void
) => {
  /** generic handler */
  const handleChange =
    (field: keyof CustomerFormData, sanitizer?: (val: string) => string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      if (sanitizer) value = sanitizer(value);
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

  /** phone */
  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = sanitizePhone(e.target.value);

      setFormData((prev) => ({
        ...prev,
        phone: value,
        phones: value ? [{ phone: value, isPrimary: true }] : [],
      }));

      const mainDigits = value.replace(/\D/g, "");
      const altDigits = formData.alternativePhone.replace(/\D/g, "");
      setIsDuplicatePhone(mainDigits === altDigits && mainDigits !== "");
    },
    [formData.alternativePhone, setFormData, setIsDuplicatePhone]
  );

  /** alternative phone */
  const handleAlternativePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = sanitizePhone(e.target.value);

      setFormData((prev) => ({
        ...prev,
        alternativePhone: value,
      }));

      const altDigits = value.replace(/\D/g, "");
      const mainDigits = formData.phone.replace(/\D/g, "");
      setIsDuplicatePhone(mainDigits === altDigits && altDigits !== "");
    },
    [formData.phone, setFormData, setIsDuplicatePhone]
  );

  return {
    /** name fields */
    handleFirstNameChange: handleChange("firstName", sanitizeName),
    handleMiddleNameChange: handleChange("middleName", sanitizeName),
    handleLastNameChange: handleChange("lastName", sanitizeName),

    /** contact */
    handlePhoneChange,
    handleAlternativePhoneChange,
    handleEmailChange: handleChange("email"),

    /** address */
    handleAddressChange: handleChange("address"),
    handleAddress2Change: handleChange("address2"),
    handleAddress3Change: handleChange("address3"),
    handleAddress4Change: handleChange("address4", sanitizePincode),
  };
};
